/**
 * One head, described once, used twice.
 *
 * A page's head is built as data. The build script serialises it into the
 * prerendered document a crawler is served, and the browser applies the same
 * object when the router moves to a page the crawler never asked for. Neither
 * side owns a second copy of a title, so a prerendered head and a navigated
 * head cannot disagree.
 *
 * Every tag this module writes is marked `data-seo`, which is also how it finds
 * the tags it wrote last time. That marker is what lets a client navigation
 * replace a prerendered head instead of appending a second one beside it.
 */

export type HeadTag =
  | { tag: "meta"; attrs: Record<string, string> }
  | { tag: "link"; attrs: Record<string, string> }
  | { tag: "jsonLd"; data: unknown };

export interface PageHead {
  title: string;
  /**
   * The `lang` the document should claim, which is a property of the page and
   * not of the shell: one bundle serves three languages, so the attribute in
   * `index.html` is only ever right for one of them.
   */
  lang: string;
  tags: HeadTag[];
}

const MANAGED_MARKER = "data-seo";

/**
 * Tags the shell ships in `index.html` and every page then overwrites. They are
 * removed by selector rather than by marker, because the shell's copies predate
 * this module and carry no marker of their own.
 */
const SHELL_SELECTORS = [
  'meta[name="description"]',
  'link[rel="canonical"]',
] as const;

function escapeAttribute(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function escapeText(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/**
 * JSON-LD is a data block rather than a script, so it is written verbatim. The
 * one sequence that could end the block early is neutralised: a `</script>`
 * inside a title would otherwise close the element and spill the rest into the
 * document as markup.
 */
function serializeJsonLd(data: unknown): string {
  return JSON.stringify(data, null, 2).replace(/<\//g, "<\\/");
}

function renderTags(
  tags: readonly HeadTag[],
  indent: string,
  marked: boolean,
): string[] {
  const marker = marked ? ` ${MANAGED_MARKER}` : "";
  const lines: string[] = [];

  for (const tag of tags) {
    if (tag.tag === "jsonLd") {
      lines.push(
        `${indent}<script${marker} type="application/ld+json">`,
        serializeJsonLd(tag.data),
        `${indent}</script>`,
      );
      continue;
    }

    const attrs = Object.entries(tag.attrs)
      .map(([name, value]) => `${name}="${escapeAttribute(value)}"`)
      .join(" ");
    lines.push(`${indent}<${tag.tag}${marker} ${attrs} />`);
  }

  return lines;
}

/** Serialises a head into the markup the prerendered document carries. */
export function renderHeadTags(head: PageHead, indent = "    "): string {
  // The title carries no marker: `document.title` is the browser's own way to
  // update it, and a marked title would be removed before it could be reused.
  return [
    `${indent}<title>${escapeText(head.title)}</title>`,
    ...renderTags(head.tags, indent, true),
  ].join("\n");
}

/**
 * Serialises tags that belong to the document rather than to the page, such as
 * a font preload. They carry no marker, because `applyHead` takes back
 * everything marked on the next navigation and a font fetched once should not
 * be un-declared when the reader clicks through to a second article.
 */
export function renderPersistentTags(
  tags: readonly HeadTag[],
  indent = "    ",
): string {
  return renderTags(tags, indent, false).join("\n");
}

/**
 * Applies a head to a live document, replacing whatever the last page left
 * behind. Called on every route change, so it has to be idempotent: applying
 * the same head twice must leave one of each tag, not two.
 */
export function applyHead(head: PageHead, target: Document): void {
  // Clearing before writing is what keeps this idempotent, and the title is
  // written afterwards because the marked tags being removed may include the
  // prerendered <title> this page arrived with.
  for (const selector of [`[${MANAGED_MARKER}]`, ...SHELL_SELECTORS]) {
    for (const element of target.head.querySelectorAll(selector)) {
      element.remove();
    }
  }

  target.title = head.title;
  // A client navigation between languages has to move this too, or a Thai page
  // reached from the English one keeps announcing itself as English.
  target.documentElement.lang = head.lang;

  for (const tag of head.tags) {
    if (tag.tag === "jsonLd") {
      const script = target.createElement("script");
      script.setAttribute(MANAGED_MARKER, "");
      script.type = "application/ld+json";
      script.textContent = serializeJsonLd(tag.data);
      target.head.appendChild(script);
      continue;
    }

    const element = target.createElement(tag.tag);
    element.setAttribute(MANAGED_MARKER, "");
    for (const [name, value] of Object.entries(tag.attrs)) {
      element.setAttribute(name, value);
    }
    target.head.appendChild(element);
  }
}
